/**
 * StrategyParameters — clean read-only display of strategy param key/value pairs.
 *
 * Props:
 *   params       - Record<string, unknown>: the parameters to display
 *   title        - optional section heading (rendered as <h3>)
 *   emptyMessage - text shown when params is empty (default: "No parameters.")
 *   layout       - 'grid' (default) | 'compact' — controls density
 *   className    - extra Tailwind classes appended to root div
 *
 * Value rendering rules:
 *   null / undefined / empty string → "—" (dimmed)
 *   number 0                        → "0"
 *   boolean                         → "true" / "false"
 *   string / number                 → String(value)
 *   object / array                  → JSON.stringify
 */

export interface StrategyParametersProps {
  params: Record<string, unknown>;
  title?: string;
  emptyMessage?: string;
  layout?: 'grid' | 'compact';
  className?: string;
}

function renderValue(value: unknown): { text: string; dim: boolean } {
  if (value === null || value === undefined || value === '') {
    return { text: '—', dim: true };
  }
  if (typeof value === 'boolean') {
    return { text: String(value), dim: false };
  }
  if (typeof value === 'number') {
    return { text: String(value), dim: false };
  }
  if (typeof value === 'string') {
    return { text: value, dim: false };
  }
  // object or array
  try {
    return { text: JSON.stringify(value), dim: false };
  } catch {
    return { text: String(value), dim: false };
  }
}

export function StrategyParameters({
  params,
  title,
  emptyMessage = 'No parameters.',
  layout = 'grid',
  className = '',
}: StrategyParametersProps) {
  const entries = Object.entries(params);

  const rootClass = ['bg-gray-800 rounded-lg border border-gray-700 p-4', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass}>
      {title && (
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
          {title}
        </h3>
      )}

      {entries.length === 0 ? (
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      ) : layout === 'compact' ? (
        <CompactLayout entries={entries} />
      ) : (
        <GridLayout entries={entries} />
      )}
    </div>
  );
}

// ── Grid layout — 2-column label/value, good breathing room ──────────────────

function GridLayout({ entries }: { entries: [string, unknown][] }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      {entries.map(([key, value]) => {
        const { text, dim } = renderValue(value);
        return (
          <div key={key} className="flex flex-col gap-0.5 min-w-0">
            <dt className="text-xs font-medium text-gray-500 truncate">{key}</dt>
            <dd
              className={[
                'text-sm font-mono break-all',
                dim ? 'text-gray-600' : 'text-gray-200',
              ].join(' ')}
            >
              {text}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

// ── Compact layout — horizontal inline pairs for table cells / tight spaces ──

function CompactLayout({ entries }: { entries: [string, unknown][] }) {
  return (
    <dl className="flex flex-wrap gap-x-4 gap-y-1.5">
      {entries.map(([key, value]) => {
        const { text, dim } = renderValue(value);
        return (
          <div key={key} className="flex items-center gap-1.5 min-w-0">
            <dt className="text-[11px] text-gray-500 shrink-0">{key}</dt>
            <dd
              className={[
                'text-[11px] font-mono break-all',
                dim ? 'text-gray-600' : 'text-gray-300',
              ].join(' ')}
            >
              {text}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
